import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Code2, Globe, ChevronDown, Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const RouterNavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-muted)'};
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const LangContainer = styled.div`
  position: relative;
  margin-right: 15px;
`;

const LangButton = styled.button`
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: #0d1117;
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  min-width: 140px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LangOption = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-muted)'};
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  text-align: left;
  transition: all 0.2s;
  font-weight: ${props => props.active ? '600' : '400'};
  width: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-main);
  }
`;

const ActionButton = styled(Link)`
  padding: 10px 24px;
  border-radius: 99px;
  border: none;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 20px -5px var(--primary-glow);
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px -5px var(--primary-glow);
  }
`;

const MobileMenuBtn = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--text-main);
  cursor: pointer;
  
  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  width: 100%;
  height: calc(100vh - 80px);
  background: var(--bg-dark);
  padding: 40px 5%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  z-index: 999;
  
  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  text-decoration: none;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-main)'};
  padding: 15px 0;
  border-bottom: 1px solid var(--glass-border);
  
  &:hover {
    color: var(--primary);
  }
`;

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <>
      <Nav scrolled={scrolled}>
        <Logo to="/">
          <Code2 size={28} color="var(--primary)" />
          <span>Nexova Dev LLC</span>
        </Logo>
        <NavLinks>
          <RouterNavLink to="/" $active={location.pathname === '/'}>{t('nav.home') || 'Home'}</RouterNavLink>
          <RouterNavLink to="/about" $active={location.pathname === '/about'}>{t('nav.about')}</RouterNavLink>
          <RouterNavLink to="/services" $active={location.pathname === '/services'}>{t('nav.services')}</RouterNavLink>
          <RouterNavLink to="/projects" $active={location.pathname === '/projects' || location.pathname.startsWith('/projects/')}>{t('nav.projects')}</RouterNavLink>
          <RouterNavLink to="/faq" $active={location.pathname === '/faq'}>FAQ</RouterNavLink>
          <RouterNavLink to="/contact" $active={location.pathname === '/contact'}>{t('nav.contact')}</RouterNavLink>
          <ThemeToggle />
          <LangContainer onMouseEnter={() => setLangOpen(true)} onMouseLeave={() => setLangOpen(false)}>
            <LangButton>
              <Globe size={16} />
              {i18n.language ? i18n.language.toUpperCase().split('-')[0] : 'EN'}
              <ChevronDown size={14} style={{ transform: langOpen ? 'rotate(180deg)' : 'none', transition: '0.3s' }} />
            </LangButton>
            <AnimatePresence>
              {langOpen && (
                <LangDropdown
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  {[
                    { code: 'en', label: 'English' },
                    { code: 'tr', label: 'Türkçe' },
                    { code: 'es', label: 'Español' }
                  ].map(lng => (
                    <LangOption
                      key={lng.code}
                      active={i18n.language && i18n.language.startsWith(lng.code)}
                      onClick={() => {
                        changeLanguage(lng.code);
                        setLangOpen(false);
                      }}
                    >
                      {lng.label}
                    </LangOption>
                  ))}
                </LangDropdown>
              )}
            </AnimatePresence>
          </LangContainer>
          <ActionButton to="/contact">
            {t('nav.start') || 'Start Project'}
          </ActionButton>
        </NavLinks>
        <MobileMenuBtn onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </MobileMenuBtn>
      </Nav >
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <MobileNavLink to="/" $active={location.pathname === '/'}>{t('nav.home') || 'Home'}</MobileNavLink>
            <MobileNavLink to="/about" $active={location.pathname === '/about'}>{t('nav.about')}</MobileNavLink>
            <MobileNavLink to="/services" $active={location.pathname === '/services'}>{t('nav.services')}</MobileNavLink>
            <MobileNavLink to="/projects" $active={location.pathname === '/projects' || location.pathname.startsWith('/projects/')}>{t('nav.projects')}</MobileNavLink>
            <MobileNavLink to="/faq" $active={location.pathname === '/faq'}>FAQ</MobileNavLink>
            <MobileNavLink to="/contact" $active={location.pathname === '/contact'}>{t('nav.contact')}</MobileNavLink>

            <div style={{ marginTop: '20px', display: 'flex', gap: '20px', alignItems: 'center' }}>
              <ThemeToggle />
              <div style={{ display: 'flex', gap: '10px' }}>
                {['en', 'tr', 'es'].map(lng => (
                  <button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    style={{
                      background: i18n.language?.startsWith(lng) ? 'var(--primary)' : 'transparent',
                      color: i18n.language?.startsWith(lng) ? 'white' : 'var(--text-muted)',
                      border: '1px solid var(--glass-border)',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    {lng.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <ActionButton to="/contact" style={{ width: 'fit-content', marginTop: '20px' }}>
              {t('nav.start') || 'Start Project'}
            </ActionButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
